import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, Observable, of, shareReplay, take, tap, throwError } from 'rxjs';
import { Logger } from '../../../@common/log/logger';
import { RegisterBody, SortOrder, UpdateOwnUserBody, UpdatePasswordBody, User, UserSort, UserType, V2AuthResource, V2UsersResource } from '../../../@generated';
import { NotificationService } from '../../notification/application/notification.service';
import { CookieService } from '../../storage/application/cookie.service';
import { LocalStorageService } from '../../storage/application/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private localStorageService = inject(LocalStorageService);
    private cookieService = inject(CookieService);
    private authResource = inject(V2AuthResource);
    private userResource = inject(V2UsersResource);
    private notificationService = inject(NotificationService);
    private router = inject(Router);
    private logger = inject(Logger);
    private platformId = inject(PLATFORM_ID);

    public user = this.cookieService.user;
    public isAuthenticated = computed(() => !!this.cookieService.token());
    public isAdmin = computed(() => this.user()?.type === UserType.Admin);

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.setupUserProfile().subscribe(profile => {
                if (profile) {
                    this.cookieService.setUser(profile)
                }
            });
        }
    }

    private setupUserProfile(): Observable<User | undefined> {
        if (!this.cookieService.token()) {
            return of(undefined);
        }

        const cachedUserProfile = this.cookieService.user();

        if (cachedUserProfile) {
            return of(cachedUserProfile);
        }

        return this.userResource.getCurrentUser().pipe(
            tap(user => this.cookieService.setUser(user)),
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get user profile failed', e);
                return of(undefined);
            })
        );
    }

    public updateProfile(userData: UpdateOwnUserBody) {
        return this.userResource.updateCurrentUser(userData).pipe(
            take(1),
            tap(user => this.cookieService.setUser(user)),
            catchError(e => {
                this.logger.apiError('Update user profile failed', e);
                if (e instanceof HttpErrorResponse && e.status === 401 || e.status === 403) {
                    this.cookieService.logout();
                }
                return throwError(() => e);
            })
        );
    }

    public getAnonymousUserId() {
        if (!this.localStorageService.getDeviceId()) {
            this.localStorageService.setDeviceId(this.generateUUID());
        }

        return this.localStorageService.getDeviceId()!;
    }

    private generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public updatePassword(passwordData: UpdatePasswordBody): Observable<any> {
        return this.authResource.updatePassword(passwordData).pipe(
            take(1),
            catchError(e => {
                this.logger.apiError('Update password failed', e);
                return throwError(() => e);
            })
        );
    }

    public register(user: RegisterBody) {
        return this.authResource.register(user).pipe(
            take(1),
            catchError(e => {
                if (e instanceof HttpErrorResponse && e.status.toString().startsWith('5')) {
                    this.logger.apiError('Register failed', e);
                }

                return throwError(() => e)
            })
        );
    }

    public login(username: string, password: string) {
        return this.authResource.login({ username, password }).pipe(
            tap(({ user, token }) => {
                this.cookieService.setUser(user)
                this.cookieService.setToken(token);
            }),
            take(1),
            catchError(e => {
                if (!(e instanceof HttpErrorResponse && e.status === 401)) {
                    this.logger.apiError('Login failed', e);
                }

                return throwError(() => e)
            })
        );
    }

    public logout() {
        this.cookieService.logout();
        this.router.navigate(['/']);
    }

    public async activateRegistration(uid: string, token: string) {
        try {
            await firstValueFrom(this.authResource.activateAccount({ uid, token }));
        } catch (e) {
            if (e instanceof HttpErrorResponse && e.status !== 404) {
                this.logger.apiError('Activate registration failed', e);
            }

            throw e;
        }
    }

    public triggerPasswordResetEmail(email: string): Observable<any> {
        return this.authResource.sendResetPasswordEmail({ email }).pipe(
            take(1),
            catchError(e => {
                if (e instanceof HttpErrorResponse && e.status.toString().startsWith('5')) {
                    this.logger.apiError('Forgot Password failed', e);
                } else {
                    console.warn('Forgot Password failed', e);
                    this.notificationService.error({ detail: 'User with this email does not exist' });
                }

                return throwError(() => e);
            })
        );
    }

    public resetPasswordUpdate(uid: string, token: string, password: string) {
        return this.authResource.resetPassword({ uid, token, new_password: password }).pipe(
            take(1),
            catchError(e => {
                this.logger.apiError('Reset password failed', e);
                return throwError(() => e)
            })
        );
    }

    public getAllUsers(): Observable<User[]> {
        return this.userResource.getUsers(1, 100, SortOrder.Ascending, UserSort.Username, undefined)
            .pipe(
                map(response => response.results),
                catchError(e => {
                    this.logger.apiError('Get all user infos failed', e);
                    return of([]);
                })
            );
    }
}
